import { LegalLinks } from "components";
import { useBrand } from "hooks/useBrand";
import { PublicPricing, usePublicPricing } from "hooks/usePublicPricing";
import { useSeo } from "hooks/useSeo";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paths from "routes/Paths";
import { getSignUpPathWithRedirect } from "routes/signInRedirect";

import type { PublicPlan } from "./pricing.types";
import PublicPlanCard from "./PublicPlanCard";

import styles from "./PricingPage.module.css";

const FREE_FEATURE_KEYS = ["publicTrees", "editors", "core"] as const;

const PRO_FEATURE_KEYS = ["everythingFree", "privateTrees", "unlimitedEditors", "keepPrivate", "support"] as const;

const TERM_KEYS = ["amount", "currency", "cycle", "cancellation", "payments"] as const;

/**
 * Which variant of a billing term to show. Only the amount row states a tax, so only it has a
 * tax-free wording; everything else reads the same either way.
 */
const detailKey = (termKey: string, hasTax: boolean): string =>
	termKey === "amount" && !hasTax ? "detailNoTax" : "detail";

const buildPlans = (pricing: PublicPricing): PublicPlan[] => [
	{
		ctaTo: Paths.signUp,
		featureKeys: FREE_FEATURE_KEYS,
		key: "free",
		// Zero in whatever currency this visitor is quoted, rather than a hardcoded "₹0" that
		// contradicts the Pro column beside it.
		price: pricing.zero,
		priceNoteKeys: ["priceNote"]
	},
	{
		ctaTo: getSignUpPathWithRedirect(Paths.plans),
		featureKeys: PRO_FEATURE_KEYS,
		isHighlighted: true,
		key: "pro",
		price: pricing.total,
		// A sale outside India is a zero-rated export, so the GST note would assert a tax the
		// customer is not charged.
		priceNoteKeys: ["recurringNote", pricing.hasTax ? "gstNote" : "noTaxNote"],
		showPeriod: true
	}
];

/**
 * The public pricing page at /plans. Signed-out visitors — including payment-gateway reviewers —
 * must be able to see the exact recurring amount and currency without creating an account, so the
 * figures come from the public plans endpoint and nothing here is gated behind sign-in. Signed-in
 * users get the account-aware PlansPage on the same path via PrivateRouter.
 */
const PricingPage = () => {
	const { t, ready } = useTranslation(["pricingPage", "common", "seo"]);
	const { brand } = useBrand();
	const pricing = usePublicPricing();
	// Namespaces load over HTTP; until this one lands `t` echoes the raw key, which would flash in
	// the browser tab. useSeo leaves the title alone for a null, so wait for it. The description
	// comes from `seo`, fetched at boot, so the meta tag is populated from first paint.
	useSeo({
		title: ready ? t("pricingPage:documentTitle") : null,
		description: t("seo:pricing.description"),
		canonicalPath: Paths.plans
	});
	const plans = buildPlans(pricing);

	return (
		<main className={styles.page}>
			<header className={styles.header}>
				<Link aria-label={t("common:brandHome", { brand })} className={styles.brand} to={Paths.root}>
					<img alt="" className={styles.brandMark} src="/assets/landing/main-logo.png" />
					<span>{brand}</span>
				</Link>
				<Link className={styles.homeLink} to={Paths.root}>
					{t("common:backToHome")}
				</Link>
			</header>

			<section className={styles.hero}>
				<div className={styles.heroInner}>
					<p className={styles.eyebrow}>{t("pricingPage:eyebrow", { brand })}</p>
					<h1>{t("pricingPage:title")}</h1>
					<div aria-hidden="true" className={styles.flourish} />
					<p className={styles.heroSubtitle}>
						{t(pricing.hasTax ? "pricingPage:subtitle" : "pricingPage:subtitleNoTax", {
							currency: pricing.currencyLabel,
							total: pricing.total
						})}
					</p>
				</div>
			</section>

			<div className={styles.body}>
				<div className={styles.plansGrid}>
					{plans.map((plan: PublicPlan) => (
						<PublicPlanCard key={plan.key} plan={plan} pricing={pricing} />
					))}
				</div>

				<section aria-labelledby="billing-terms-heading" className={styles.termsCard}>
					<h2 className={styles.cardHeading} id="billing-terms-heading">
						{t("pricingPage:terms.heading")}
					</h2>
					<dl className={styles.terms}>
						{TERM_KEYS.map((key: string) => (
							<div key={key} className={styles.term}>
								<dt>{t(`pricingPage:terms.${key}.term`)}</dt>
								<dd>
									{t(`pricingPage:terms.${key}.${detailKey(key, pricing.hasTax)}`, {
										base: pricing.base,
										brand,
										currency: pricing.currencyLabel,
										tax: pricing.tax,
										taxRate: pricing.taxRate,
										total: pricing.total
									})}
								</dd>
							</div>
						))}
					</dl>
					<p className={styles.termsFootnote}>
						<Trans
							components={{
								support: <Link to={Paths.helpSupport} />,
								terms: <Link to={Paths.termsOfService} />
							}}
							i18nKey="terms.footnote"
							ns="pricingPage"
						/>
					</p>
				</section>
			</div>

			<footer className={styles.footer}>
				<span>{t("common:copyright")}</span>
				<nav aria-label={t("common:sitePagesLabel")} className={styles.footerNav}>
					<LegalLinks />
				</nav>
			</footer>
		</main>
	);
};

export default PricingPage;
